import { useCallback } from 'react'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { dictionaryService } from '@/api/services/dictionary.service'
import type { DictionaryResponse, RemoveDictionaryWordOutcome } from '@/api/types/dictionary'
import { logger } from '@/utils/logger'

/**
 * Reads the dictionary from the server into the cache and says whether `word` is no longer in it.
 * A read that fails too means nothing is known, so the remove stays a failure.
 */
async function isGoneFromServer(queryClient: QueryClient, word: string): Promise<boolean> {
  // Offline the server can't be asked, and the read would only hold the failure toast back.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false

  try {
    const fresh = await queryClient.fetchQuery({
      queryKey: queryKeys.dictionary.all,
      queryFn: () => dictionaryService.getDictionary(),
      staleTime: 0,
      // React Query pauses a read it thinks is offline until the connection is back, which would
      // leave the remove unanswered. Sent at once, a failing read fails and the toast shows.
      networkMode: 'always',
    })

    return !fresh.customWords.includes(word)
  } catch (error) {
    logger.error('Reading the dictionary after a failed remove failed', error)

    return false
  }
}

/**
 * Removes one custom word and waits for the backend before touching the cache, so a failed remove
 * never hides the word. When the remove fails, the dictionary is read again: a word another editor
 * already removed is refused upstream, yet it is gone, which is what the user asked for (WMPCM-12996).
 * Once the word is out, a read already running is cancelled so it cannot bring it back, and the
 * dictionary is read again so other editors' changes still show.
 *
 * The backend keeps capitalization variants as separate words (`thier` and `Thier`), so the word is
 * sent exactly as asked. Matching it to a differently capitalized entry could remove another word.
 */
export function useRemoveDictionaryWord() {
  const queryClient = useQueryClient()

  return useCallback(
    async (word: string): Promise<RemoveDictionaryWordOutcome> => {
      try {
        await dictionaryService.removeWord(word)
      } catch (error) {
        logger.error('Removing a dictionary word failed', error)

        return (await isGoneFromServer(queryClient, word))
          ? { status: 'removed' }
          : { status: 'failed' }
      }

      // A read already running was sent before this word was removed, so it would come back with
      // it and put it back. Cancel it before dropping the word.
      void queryClient.cancelQueries({ queryKey: queryKeys.dictionary.all })

      queryClient.setQueryData<DictionaryResponse>(queryKeys.dictionary.all, (current) =>
        current
          ? { ...current, customWords: current.customWords.filter((stored) => stored !== word) }
          : current
      )

      // Read again so a change another editor made in the meantime still shows. The cancelled
      // read may have been carrying it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.dictionary.all })

      return { status: 'removed' }
    },
    [queryClient]
  )
}
